import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api';
import { queryKeys } from '@/api/query-keys';
import { Badge, Button, Drawer, Icon, Textarea } from '@/ui';
import { useUiPrefs } from '@/store/ui-prefs';

/**
 * Left-side "ask Atlas" composer.
 *
 * Opened when the user types a question into the topbar search bar. Shows
 * the seed question for refinement, sends it as a chat message, then hands
 * off to the right-side chat panel where the conversation continues.
 *
 * A future iteration could make this a fully persistent left-side
 * discovery agent (separate session, search-tuned prompt). Today it's the
 * fast path: compose → send → see the answer in the main chat.
 */
export function SearchChatPanel() {
  const { searchChatOpen, searchChatSeed, closeSearchChat, setChatOpen } = useUiPrefs();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(searchChatSeed);

  // Reset draft each time the panel opens with a new seed.
  useEffect(() => {
    if (searchChatOpen) setDraft(searchChatSeed);
  }, [searchChatOpen, searchChatSeed]);

  const ask = useMutation({
    mutationFn: async (message: string) => {
      // Make sure we're not in intake mode for a stray question — drop back
      // to default and skip clearing so the existing intake conversation
      // isn't wiped if the user opens it again later.
      await api.chat.setMode('default', { clear: false });
      return api.chat.send({ message });
    },
    onSuccess: (out) => {
      queryClient.setQueryData(queryKeys.chat.session, (prev: unknown) => {
        if (!prev || typeof prev !== 'object') return prev;
        const session = prev as { messages?: unknown[] };
        return {
          ...session,
          messages: [...(session.messages ?? []), ...out.new_messages],
        };
      });
      closeSearchChat();
      setChatOpen(true);
    },
  });

  const onSubmit = () => {
    const text = draft.trim();
    if (!text || ask.isPending) return;
    ask.mutate(text);
  };

  return (
    <Drawer open={searchChatOpen} onClose={closeSearchChat} width={420} side="left">
      <header
        style={{
          padding: 'var(--apm-card-pad)',
          borderBottom: '1px solid var(--apm-line)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              color: 'var(--apm-muted)',
              marginBottom: 4,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Icon name="chat" size={12} />
            Ask Atlas
            <Badge tone="info" size="sm">Quick</Badge>
          </div>
          <div
            style={{
              fontFamily: 'var(--apm-font-display)',
              fontSize: 'var(--apm-text-xl)',
              letterSpacing: '-0.01em',
            }}
          >
            Search agent
          </div>
        </div>
        <Button variant="ghost" icon={<Icon name="x" size={14} />} onClick={closeSearchChat} />
      </header>

      <div
        style={{
          flex: 1,
          padding: 'var(--apm-card-pad)',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--apm-inner-gap)',
        }}
      >
        <div style={{ fontSize: 13, color: 'var(--apm-muted)', lineHeight: 1.55 }}>
          Refine your question below, then press <strong>Ask Atlas</strong>. The
          answer (and any follow-up) lives in the main chat on the right.
        </div>

        <Textarea
          value={draft}
          onChange={setDraft}
          placeholder="e.g. Which projects involve PHI? Who owns the AP Aging project?"
          rows={5}
        />

        {ask.isError && (
          <div
            role="alert"
            style={{
              padding: '8px 12px',
              background: 'var(--apm-risk-soft)',
              color: 'var(--apm-risk)',
              borderRadius: 'var(--apm-radius)',
              fontSize: 12.5,
            }}
          >
            Couldn't reach Atlas. Check Help → Atlas status, then try again.
          </div>
        )}

        <div
          style={{
            display: 'flex',
            gap: 8,
            justifyContent: 'flex-end',
            marginTop: 'auto',
          }}
        >
          <Button variant="ghost" onClick={closeSearchChat}>Cancel</Button>
          <Button
            variant="primary"
            onClick={onSubmit}
            disabled={!draft.trim() || ask.isPending}
          >
            {ask.isPending ? 'Asking…' : 'Ask Atlas'}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
